import type { FC } from 'react'
import { useWatch } from 'react-hook-form'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { useExperienceShellFormContext } from '@/features/experience-shell/hooks/use-experience-shell-form-context.ts'
import { useTranslation } from '@/translations/i18n'
import { useChangeStep } from '../hooks/use-change-step'
import { type Experience, ShellStepKeyName } from '../types'
import {
	checkIsItineraryLocked,
	checkIsItineraryValid,
	checkIsLegalLocked,
	stepToIcon,
	stepToLabel
} from '../utils'
import { Progress } from './progress'

export const StepsNavigation: FC<{
	currentStep: ShellStepKeyName
	experienceId: number
	galleryImagesCount: number
	itineraries: Experience['itineraries']
	isUpgrade: boolean
}> = ({
	currentStep,
	experienceId,
	galleryImagesCount,
	itineraries,
	isUpgrade
}) => {
	const { t } = useTranslation()
	const { control } = useExperienceShellFormContext(isUpgrade)
	const experienceCategoryWatch = useWatch({
		control,
		name: 'experience.categoryKeyName'
	})
	const changeStep = useChangeStep({ experienceId, currentStep, isUpgrade })
	const stepsToShow = ShellStepKeyName.options.filter(
		(step) => step !== 'settings'
	)
	return (
		<aside className='flex flex-col gap-2 border-neutral-300 border-r px-4 py-8'>
			{stepsToShow.map((step) => {
				const StepIcon = stepToIcon[step]
				const isItineraryLocked =
					step === 'itinerary' && checkIsItineraryLocked(itineraries)
				const isLegalLocked =
					step === 'legal' &&
					checkIsLegalLocked(experienceCategoryWatch)
				const isPhotosStepInvalid =
					isUpgrade &&
					currentStep === ShellStepKeyName.enum.photos &&
					galleryImagesCount === 0
				const isItineraryStepInvalid =
					isUpgrade &&
					currentStep === ShellStepKeyName.enum.itinerary &&
					itineraries.some(
						(itinerary) => !checkIsItineraryValid(itinerary)
					)

				return (
					<ButtonLegacy
						className={`h-11 justify-between px-2 font-normal text-neutral-600 no-underline transition-[background] hover:bg-neutral-50 [&>svg]:h-auto [&>svg]:w-auto [&_svg]:h-6 [&_svg]:w-6 ${
							currentStep === step
								? 'bg-neutral-50 font-medium text-neutral-900 outline-2 outline-neutral-900'
								: ''
						}`}
						disabled={
							isLegalLocked ||
							isItineraryLocked ||
							isPhotosStepInvalid ||
							isItineraryStepInvalid ||
							changeStep.editExperienceMutation.isPending ||
							changeStep.editStepMutation.isPending
						}
						key={step}
						onClick={() => {
							if (isPhotosStepInvalid || isItineraryStepInvalid) {
								return
							}
							changeStep.handleChangeStep({ stepToGoTo: step })
						}}
						type='button'
						variant='tertiary'
					>
						<div className='flex items-center gap-2'>
							<StepIcon />
							{t(stepToLabel[step])}
						</div>
						<Progress
							experienceId={experienceId}
							galleryImagesCount={galleryImagesCount}
							isItineraryLocked={isItineraryLocked}
							isLegalLocked={isLegalLocked}
							isUpgrade={isUpgrade}
							step={step}
						/>
					</ButtonLegacy>
				)
			})}
		</aside>
	)
}
